<template>
  <div class="d-flex align-items-center">
    <div class="d-lg-none col-5">
      <SortTableMobileFirstColumnComponent :nbRows="nbColumns" :rows="column" />
    </div>
    <div class="col-lg-12 col-8 font-size">
      <button
        v-if="displayAbsenceStatus()"
        v-tooltip="{ title: tooltipTitle }"
        type="button"
        :class="
          'badge ' +
          isAbsenceJustified(row[column.index ? column.index : column.name]) +
          (!trainingCourse.depositProofAbsence && !trainingCourse.isStudeaManager ? ' disabled' : '')
        "
        @click="manageAbsence(row[column.index ? column.index : column.name])"
      >
        {{ tooltipTitle }}
      </button>
      <router-link
        v-else-if="displayPlanningStatus()"
        v-tooltip="{ title: tooltipTitle }"
        :to="{ name: 'studeaManagerPlanningSignaturetEdit', params: { alias: type, id: row[8] } }"
        :class="
          'badge ' +
          isAbsenceJustified(row[column.index ? column.index : column.name]) +
          (!trainingCourse.depositProofAbsence && !trainingCourse.isStudeaManager ? ' disabled' : '')
        "
        >{{ tooltipTitle }}
      </router-link>
      <div v-else>--</div>
      <button
        v-if="row[9] && type === 'absence'"
        v-tooltip="{ title: $t('absence.justification.status.download') }"
        class="ms-2 btn-icon"
        @click="downloadFile"
      >
        <FontAwesomeIcon icon="fa-light fa-download" />
      </button>
    </div>
  </div>
</template>

<script>
import SortTableMobileFirstColumnComponent from "@/components/table/sortTable/SortTableMobileFirstColumnComponent.vue";
import { mapState } from "pinia";
import { useTrainingCourseStore } from "@/store/training-course/training-course";

export default {
  name: "SortTableAbsenceStatusComponent",
  components: { SortTableMobileFirstColumnComponent },
  props: {
    column: {
      type: Object,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    row: {
      type: Object,
      required: true,
    },
    nbColumns: {
      type: Number,
      required: true,
    },
    idKey: {
      type: Number,
      default: 0,
      required: false,
    },
    type: {
      type: String,
      required: true,
    },
  },
  emits: ["verifyAbsence", "justifyAbsence", "downloadJustificativeFile"],
  data() {
    return {};
  },
  computed: {
    ...mapState(useTrainingCourseStore, {
      trainingCourse: (store) => store.trainingCourse,
    }),
    tooltipTitle() {
      return this.setAbsenceBadgeText(this.row[this.column.index ? this.column.index : this.column.name]);
    },
  },
  methods: {
    isAbsenceJustified(isJustified) {
      if (this.type === "absence") {
        return this.getAbsenceColor(isJustified);
      }

      if (this.type === "planning") {
        return this.getPlanningColor(isJustified);
      }
    },

    getAbsenceColor(isJustified) {
      const isInvalid = this.row[this.columns.length + 2];
      const hasProof = this.row[this.columns.length + 1] && Object.keys(this.row[this.columns.length + 1]).length > 0;
      const { depositProofAbsence, isStudeaManager } = this.trainingCourse;

      if (isInvalid) {
        return "bg-danger";
      }

      if (isJustified) {
        return "bg-success";
      }

      if (hasProof) {
        return depositProofAbsence || (!depositProofAbsence && isStudeaManager) ? "bg-primary" : "bg-danger";
      }

      return depositProofAbsence ? "bg-warning" : "bg-danger";
    },

    getPlanningColor(isJustified) {
      if (this.row[9]) {
        return "bg-danger";
      }

      if (isJustified) {
        const parts = this.row[6].split("/");
        return parts[0] !== parts[1] ? "bg-primary" : "bg-success";
      }

      return "bg-warning";
    },

    setAbsenceBadgeText(isJustified) {
      if (this.type === "absence") {
        return this.getAbsenceStatus(isJustified);
      }

      if (this.type === "planning") {
        return this.getPlanningStatus(isJustified);
      }
    },

    getAbsenceStatus(isJustified) {
      const isInvalid = this.row[this.columns.length + 2];
      const hasProof = this.row[this.columns.length + 1] && Object.keys(this.row[this.columns.length + 1]).length > 0;
      const isStudeaManagerRoute = this.$route.path.includes("studea-manager");
      const { depositProofAbsence, isStudeaManager } = this.trainingCourse;

      if (isInvalid) {
        return this.$t("absence.justification.status.invalid");
      }

      if (isJustified) {
        return this.$t("absence.justification.status.justified");
      }

      if (hasProof) {
        if (isStudeaManagerRoute || isStudeaManager) {
          return this.$t("absence.justification.status.to_verify");
        }
        return depositProofAbsence
          ? this.$t("absence.justification.status.waiting_for_validation")
          : this.$t("absence.not_justify");
      }

      if (isStudeaManagerRoute) {
        return this.$t("absence.justification.status.waiting_proof");
      }
      return depositProofAbsence ? this.$t("absence.submit_proof") : this.$t("absence.not_justify");
    },

    getPlanningStatus(isJustified) {
      if (this.row[9]) {
        return this.$t("absence.status.cancel");
      }
      if (isJustified) {
        const parts = this.row[6].split("/");
        return parts[0] !== parts[1]
          ? this.$t("absence.status.with_absence")
          : this.$t("absence.signature.already_validate");
      }
      return this.$t("absence.status.to_valid");
    },

    manageAbsence(isJustified) {
      if (
        !isJustified &&
        (!this.row[this.columns.length + 1] ||
          (this.row[this.columns.length + 1] && !Object.keys(this.row[this.columns.length + 1])) ||
          this.row[this.columns.length + 2])
      ) {
        this.$emit("justifyAbsence", this.row[this.idKey]);
      } else if (this.row[this.columns.length + 1] && Object.keys(this.row[this.columns.length + 1]) && !isJustified) {
        this.$emit("verifyAbsence", this.row[this.idKey]);
      }
    },

    downloadFile() {
      if (this.row[9] && Object.keys(this.row[9])) {
        this.$emit("downloadJustificativeFile", this.row[9]);
      }
    },

    displayAbsenceStatus() {
      return this.type === "absence" && this.row[7] === "absence";
    },

    displayPlanningStatus() {
      return (
        this.trainingCourse.useAttendanceConfirmation && this.type === "planning" && new Date(this.row[0]) < new Date()
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.badge {
  width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  border: none;

  &.disabled {
    cursor: default;
  }

  &:hover {
    color: #fff;
  }

  @include up-to-lg {
    width: auto;
    max-width: 130px;
  }
}
</style>
